/**
 * Timeline state hook.
 *
 * Provides a fully expanded, flat timeline view. All descendant spans are
 * always visible — there is no drill-down navigation. Selection is key-based,
 * using the unique tree-position key from each SwimlaneRow.
 *
 * Selection state is owned by the caller via `selected` / `onSelect` props,
 * so both apps can persist it however they like (URL params, Zustand, etc.).
 */

import { useCallback, useMemo } from "react";

import type { Timeline, TimelineSpan } from "../core";
import { computeFlatSwimlaneRows, type SwimlaneRow } from "../swimlaneRows";

// =============================================================================
// Types
// =============================================================================

export interface TimelineState {
  /** The root TimelineSpan (always the timeline root). */
  node: TimelineSpan;
  /** Flat swimlane rows for all descendants. */
  rows: SwimlaneRow[];
  /** Currently selected row key, or null. */
  selected: string | null;
  /** Select a row by key, or null to clear. Pass `{ preserveDeepLink: true }`
   *  for message-resolution-driven selection changes so the URL `?event=` /
   *  `?message=` params survive for the in-flight imperative scroll. */
  select: (key: string | null, options?: SelectOptions) => void;
  /** Clear the selection (returns to default root selection). */
  clearSelection: () => void;
}

// =============================================================================
// Branch Lookup (used by branch marker popover)
// =============================================================================

export interface BranchLookupResult {
  /** The span that owns the branches. */
  owner: TimelineSpan;
  /** Matching branches with their 1-indexed position. */
  branches: Array<{ branch: TimelineSpan; index: number }>;
}

/**
 * Finds all branches matching a branchedFrom UUID anywhere in the span tree.
 * Returns the owning span and matching branches.
 */
export function findBranchesByBranchedFrom(
  node: TimelineSpan,
  branchedFrom: string
): BranchLookupResult | null {
  // Check this node's branches
  const matches: Array<{ branch: TimelineSpan; index: number }> = [];
  for (let i = 0; i < node.branches.length; i++) {
    const branch = node.branches[i]!;
    if (branch.branchedFrom === branchedFrom) {
      matches.push({ branch, index: i + 1 });
    }
  }
  if (matches.length > 0) {
    return { owner: node, branches: matches };
  }

  // Recurse into child spans
  for (const item of node.content) {
    if (item.type === "span") {
      const found = findBranchesByBranchedFrom(item, branchedFrom);
      if (found) return found;
    }
  }

  return null;
}

// =============================================================================
// Hook
// =============================================================================

export interface TimelineOptions {
  /** Include utility agents in the swimlane rows. Default: false. */
  includeUtility?: boolean;
  /** Show branches as swimlane rows. Default: false. */
  showBranches?: boolean;
  /** Position branches at their fork point instead of wall-clock time. Default: false. */
  forkRelative?: boolean;
  /** Show branches whose content is only structural events. Default: false. */
  showEmptyBranches?: boolean;
}

export interface SelectOptions {
  /** When true, leave the URL `?event=` / `?message=` deep-link params
   *  intact. Used by message-resolution selection changes so the in-flight
   *  imperative scroll keeps its target — row-click selections (which
   *  invalidate any prior deep link) clear them. */
  preserveDeepLink?: boolean;
  /** When true, do not reset scroll position. Used by inline fork navigators
   *  so the clicked widget stays where it was on screen. */
  preserveScroll?: boolean;
}

/** URL search-param keys used as transcript scroll-target deep links. */
export const kDeepLinkParams = ["event", "message"] as const;

/** Removes the deep-link search params (`?event=`, `?message=`) from the
 *  given `URLSearchParams`. Hosts whose `onSelect` adapter writes to URL
 *  state should call this when `preserveDeepLink` is not set, so any
 *  stale deep-link target doesn't fight the new selection's scroll. */
export function clearDeepLinkParams(params: URLSearchParams): URLSearchParams {
  for (const key of kDeepLinkParams) params.delete(key);
  return params;
}

export interface UseTimelineProps {
  /** Currently selected row key (externally managed). Null = root selection. */
  selected: string | null;
  /** Called when a row is selected. Null = clear selection. */
  onSelect: (key: string | null, options?: SelectOptions) => void;
}

/**
 * Timeline state hook.
 *
 * Computes a fully expanded flat list of swimlane rows from the timeline root.
 * Selection is driven by the caller via `props.selected` / `props.onSelect`.
 */
export function useTimeline(
  timeline: Timeline,
  options?: TimelineOptions,
  props?: UseTimelineProps
): TimelineState {
  const includeUtility = options?.includeUtility ?? false;
  const showBranchesOption = options?.showBranches ?? false;

  const selectedParam = props?.selected ?? null;
  const onSelect = props?.onSelect;

  // Force branches on when the URL selection references a branch row,
  // so that reloading a page with a branch selected works even if the
  // persistent showBranches preference is off.
  const showBranches =
    showBranchesOption ||
    (selectedParam !== null && /\/branch-/.test(selectedParam));

  // Always use the root node
  const node = timeline.root;

  // Compute flat swimlane rows for the entire tree
  const rows = useMemo(
    () => computeFlatSwimlaneRows(node, { includeUtility, showBranches }),
    [node, includeUtility, showBranches]
  );

  // Default selection: explicit param > root row key.
  const selected = useMemo(() => {
    if (selectedParam !== null) return selectedParam;
    return rows[0]?.key ?? null;
  }, [selectedParam, rows]);

  const select = useCallback(
    (key: string | null, selectOptions?: SelectOptions) => {
      onSelect?.(key, selectOptions);
    },
    [onSelect]
  );

  const clearSelection = useCallback(() => {
    onSelect?.(null);
  }, [onSelect]);

  return {
    node,
    rows,
    selected,
    select,
    clearSelection,
  };
}
